import React, { useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Radio, Plane, Satellite, PanelLeftClose, PanelLeftOpen, History, ChevronDown } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { useLocalStorage } from "@hooks/index";
import { useMediaQuery } from "@hooks/index";

interface NavItem {
    to: string;
    label: string;
    icon: React.ReactNode;
}

interface NavGroup {
    label: string;
    icon: React.ReactNode;
    prefix: string;
    children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => "children" in entry;

const navEntries: NavEntry[] = [
    { to: "/", label: "Dashboard", icon: <Home size={20} /> },
    { to: "/satellites", label: "Satellites", icon: <Satellite size={20} /> },
    { to: "/airlines", label: "Airlines", icon: <Plane size={20} /> },
    {
        label: "Legacy",
        icon: <History size={20} />,
        prefix: "/legacy",
        children: [
            { to: "/legacy", label: "Dashboard", icon: <Home size={16} /> },
            { to: "/legacy/beams", label: "Beams", icon: <Radio size={16} /> },
        ],
    },
];

function Sidebar(): React.ReactElement {
    const location = useLocation();
    const [collapsed, setCollapsed] = useLocalStorage("sidebar-collapsed", false);
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [legacyOpen, setLegacyOpen] = useLocalStorage("sidebar-legacy-open", false);

    const isCollapsed = isMobile || collapsed;

    useEffect(() => {
        if (isMobile) {
            setMobileOpen(false);
        }
    }, [location.pathname, isMobile]);

    const toggleSidebar = useCallback(() => {
        if (isMobile) {
            setMobileOpen(prev => !prev);
        } else {
            setCollapsed(prev => !prev);
        }
    }, [isMobile, setCollapsed]);

    const handleOverlayClick = useCallback(() => {
        setMobileOpen(false);
    }, []);

    const sidebarWidth = isCollapsed && !mobileOpen ? "w-16" : "w-56";

    return (
        <>
            {isMobile && mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 transition-opacity"
                    onClick={handleOverlayClick}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`
                    ${isMobile ? "fixed z-50 h-full" : "relative"}
                    ${isMobile && !mobileOpen ? "-translate-x-full" : "translate-x-0"}
                    ${isMobile && mobileOpen ? "w-56" : sidebarWidth}
                    flex flex-col bg-slate-900 border-r border-slate-700/50
                    transition-all duration-300 ease-in-out shrink-0
                `}
                aria-label="Sidebar navigation"
            >
                <div className={`flex items-center h-14 border-b border-slate-700/50 ${isCollapsed && !mobileOpen ? "justify-center px-2" : "justify-between px-4"}`}>
                    {(!isCollapsed || mobileOpen) && (
                        <Link
                            to="/"
                            className="text-base font-bold text-cyan-400 hover:text-cyan-300 transition-colors truncate"
                        >
                            NEO-SAT
                        </Link>
                    )}
                    <button
                        onClick={toggleSidebar}
                        className="p-1.5 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
                        aria-label={isCollapsed && !mobileOpen ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {isCollapsed && !mobileOpen
                            ? <PanelLeftOpen size={18} />
                            : <PanelLeftClose size={18} />
                        }
                    </button>
                </div>

                <nav className="flex-1 py-3 overflow-hidden" aria-label="Main navigation">
                    <ul className="space-y-1 px-2">
                        {navEntries.map((entry) => {
                            if (isGroup(entry)) {
                                const groupActive = location.pathname.startsWith(entry.prefix);
                                const expanded = isCollapsed && !mobileOpen ? false : legacyOpen || groupActive;

                                const groupBtnClass = [
                                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors group relative w-full",
                                    groupActive ? "text-cyan-400" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800",
                                    isCollapsed && !mobileOpen ? "justify-center" : "",
                                ].join(" ");

                                return (
                                    <li key={entry.prefix}>
                                        <button
                                            type="button"
                                            onClick={() => setLegacyOpen((prev: boolean) => !prev)}
                                            className={groupBtnClass}
                                        >
                                            <span className="shrink-0">{entry.icon}</span>
                                            {(!isCollapsed || mobileOpen) && (
                                                <>
                                                    <span className="truncate flex-1 text-left">{entry.label}</span>
                                                    <ChevronDown
                                                        size={14}
                                                        className={[
                                                            "shrink-0 transition-transform duration-200",
                                                            expanded ? "rotate-180" : "",
                                                        ].join(" ")}
                                                    />
                                                </>
                                            )}
                                            {isCollapsed && !mobileOpen && (
                                                <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-slate-800 text-slate-200 text-xs font-medium opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50 shadow-lg">
                                                    {entry.label}
                                                </span>
                                            )}
                                        </button>
                                        {expanded && (
                                            <ul className="mt-1 space-y-0.5 pl-4">
                                                {entry.children.map((child) => {
                                                    const childActive = location.pathname === child.to;
                                                    const childClass = [
                                                        "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                                        childActive ? "bg-cyan-500/10 text-cyan-400" : "text-slate-500 hover:text-slate-200 hover:bg-slate-800/60",
                                                    ].join(" ");
                                                    return (
                                                        <li key={child.to}>
                                                            <Link to={child.to} className={childClass} {...(childActive ? { "aria-current": "page" as const } : {})}>
                                                                <span className="shrink-0">{child.icon}</span>
                                                                <span className="truncate">{child.label}</span>
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </li>
                                );
                            }

                            const isActive = entry.to === "/"
                                ? location.pathname === "/"
                                : location.pathname.startsWith(entry.to);

                            const linkClass = [
                                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors group relative",
                                isActive ? "bg-cyan-500/10 text-cyan-400" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800",
                                isCollapsed && !mobileOpen ? "justify-center" : "",
                            ].join(" ");

                            return (
                                <li key={entry.to}>
                                    <Link
                                        to={entry.to}
                                        className={linkClass}
                                        {...(isActive ? { "aria-current": "page" as const } : {})}
                                    >
                                        <span className="shrink-0">{entry.icon}</span>
                                        {(!isCollapsed || mobileOpen) && (
                                            <span className="truncate">{entry.label}</span>
                                        )}
                                        {isCollapsed && !mobileOpen && (
                                            <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-slate-800 text-slate-200 text-xs font-medium opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50 shadow-lg">
                                                {entry.label}
                                            </span>
                                        )}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                <div className={`border-t border-slate-700/50 p-2 ${isCollapsed && !mobileOpen ? "flex justify-center" : "px-3"}`}>
                    <ThemeToggle />
                </div>
            </aside>

            {isMobile && !mobileOpen && (
                <button
                    onClick={toggleSidebar}
                    className="fixed top-3 left-3 z-30 p-2 rounded-lg bg-slate-900 border border-slate-700/50 text-slate-400 hover:text-slate-200 shadow-lg transition-colors"
                    aria-label="Open sidebar"
                >
                    <PanelLeftOpen size={20} />
                </button>
            )}
        </>
    );
}

export default Sidebar;
